import type {
  ModelCapabilities,
  ProviderInstanceId,
  ProviderOptionSelection,
} from "@amoreg/contracts";

/**
 * The reasoning effort a person last chose for each model, kept in this
 * browser. Options live per provider instance in the composer draft, so
 * without this a model switch would carry the previous model's effort
 * (Medium on Amoreg becoming Medium on Amoreg Pro). Instead a switch lands on
 * the effort this person uses with that model, or the model's own default.
 */
const STORAGE_KEY = "amoreg:model-effort-preferences:v1";

type Preferences = Record<string, string>;

const preferenceKey = (instanceId: ProviderInstanceId, model: string) => `${instanceId}:${model}`;

function readAll(): Preferences {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Preferences)
      : {};
  } catch {
    return {};
  }
}

export function readModelEffortPreference(
  instanceId: ProviderInstanceId,
  model: string,
): string | null {
  const value = readAll()[preferenceKey(instanceId, model)];
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function rememberModelEffortPreference(
  instanceId: ProviderInstanceId,
  model: string,
  effort: string,
): void {
  try {
    const next = { ...readAll(), [preferenceKey(instanceId, model)]: effort };
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode or a full quota: the preference is a convenience, not state.
  }
}

/** The model's reasoning control: its first select, as the traits trigger reads it. */
export function effortDescriptorOf(caps: ModelCapabilities) {
  return caps.optionDescriptors?.find((descriptor) => descriptor.type === "select") ?? null;
}

/**
 * The options a thread takes when it switches to `caps`' model: what it had,
 * minus any value the new model does not offer (Ultrafast does not survive a
 * trip through Pro to come back unasked), with the effort replaced by the
 * remembered preference when the new model offers it — otherwise left unset so
 * the model's default applies.
 */
export function optionsForModelSwitch(input: {
  readonly caps: ModelCapabilities;
  readonly currentOptions: ReadonlyArray<ProviderOptionSelection> | undefined;
  readonly preferredEffort: string | null;
}): ReadonlyArray<ProviderOptionSelection> {
  const descriptors = input.caps.optionDescriptors ?? [];
  const offered = (input.currentOptions ?? []).filter((option) => {
    const descriptor = descriptors.find((candidate) => candidate.id === option.id);
    return (
      descriptor === undefined ||
      descriptor.type === "boolean" ||
      descriptor.options.some((choice) => choice.id === option.value)
    );
  });
  const effort = effortDescriptorOf(input.caps);
  if (!effort || effort.type !== "select") return offered;
  const others = offered.filter((option) => option.id !== effort.id);
  const preferred = effort.options.find(
    (option) =>
      input.preferredEffort !== null &&
      (option.id === input.preferredEffort || option.formerIds?.includes(input.preferredEffort)),
  );
  return preferred ? [...others, { id: effort.id, value: preferred.id }] : others;
}
